import { Component, Input, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize, Subject, takeUntil } from 'rxjs';
import { MerchantChangeTaskDetail, MerchantChangeHistoryDetail } from '../../../../../models/task.model';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { TaskResolutionEnum } from '@olv/ui-shared/enums/task.enums';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { StateBadgeComponent } from '@olv/ui-shared/ui-shared/state-badge/state-badge.component';
import { MatDividerModule } from '@angular/material/divider';
import { ButtonComponent, ButtonState, toButtonState } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-merchant-change-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    MatTooltipModule,
    HasPermissionDirective,
    StateBadgeComponent,
    MatDividerModule,
    ButtonComponent,
  ],
  templateUrl: './merchant-change-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./merchant-change-detail.component.scss'],
})
export class MerchantChangeDetailComponent implements OnDestroy {
  private merchantService = inject(MerchantService);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  protected readonly formatDateStandard = formatDateStandard;

  @Input() detail?: MerchantChangeTaskDetail;
  @Input() canResolve = false;
  @Input() initiatedBy?: string;
  @Input() preparedDate?: Date;
  @Input() isHistory = false;
  @Input() resolution?: string;
  @Input() status?: string;
  @Input() resolvedBy?: string;
  @Input() resolvedAt?: Date;
  @Input() comment!: string | undefined;
  @Input() taskId?: string;
  @Input() hideHeader = false;
  @Output() closed = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();

  showOTP = false;
  isProcessing = false;
  errorMessage = '';

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    this.closed.emit();
  }

  getChangedFieldLabel(field: string): string {
    const fieldLabels: Record<string, string> = {
      name: 'Merchant Name',
      profile: 'Profile',
      feeProfile: 'Fee Profile',
      logo: 'Logo',
      status: 'Status',
    };
    return fieldLabels[field] || field;
  }

  getHistoryDetail(): MerchantChangeHistoryDetail | undefined {
    return this.isHistory ? (this.detail as MerchantChangeHistoryDetail) : undefined;
  }

  getResolutionClass(): string {
    if (!this.resolution) return '';
    switch (this.resolution.toLowerCase()) {
      case 'approved':
        return 'resolution-approved';
      case 'rejected':
        return 'resolution-rejected';
      case 'cancelled':
        return 'resolution-cancelled';
      default:
        return '';
    }
  }

  getStatusClass(): string {
    if (!this.resolution) return 'status-pending';

    switch (this.resolution.toLowerCase()) {
      case 'approved':
        return 'status-completed';
      case 'rejected':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  }

  get acknowledgeButtonState(): ButtonState {
    return toButtonState(this.isProcessing, !this.canResolve);
  }

  onApprove(): void {
    this.showOTP = true;
    this.errorMessage = '';
  }

  onReject(): void {
    this.resolveTask(TaskResolutionEnum.Rejected);
  }

  protected resolveTask(resolution: TaskResolutionEnum): void {
    if (!this.taskId || !this.detail?.merchantId) return;

    this.isProcessing = true;
    this.errorMessage = '';

    const dto = {
      comment: this.comment?.trim() || undefined,
    };

    this.merchantService
      .acknowledgeMerchantTask(this.detail?.merchantId, this.taskId, dto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isProcessing = false)),
      )
      .subscribe({
        next: () => {
          console.log('Merchant change acknowledged successfully.');
          this.refresh.emit();
          this.onClose();
          this.showOTP = false;
        },
        error: (err) => {
          console.error('Failed to acknowledge merchant change:', err);
          this.errorMessage = err.error?.message || 'Failed to acknowledge merchant change';
        },
      });
  }

  protected readonly TaskResolutionEnum = TaskResolutionEnum;
}
